import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { api } from '@/api/client'
import type { MeResponse, Role } from '@/api/types'

// Auth state + actions. Login/TOTP/logout are exposed by the backend as JSON endpoints; the
// session itself is the existing Redis cookie. Unlike ghcboot this store also owns the /api/v1/me
// bootstrap (wwboot has no separate conventionContext store) — the router guard, layouts, and the
// edit affordances all read `user` from here.
export const useAuthStore = defineStore('auth', () => {
  const user = ref<MeResponse | null>(null)
  const loaded = ref(false)
  const isAuthenticated = computed(() => !!user.value?.authenticated)
  const role = computed<Role>(() => user.value?.role ?? 'ANON')
  const isAdmin = computed(() => role.value === 'ADMIN')

  // Dedupe: App.vue and the router guard both call loadMe() on startup. Cache the in-flight
  // promise and short-circuit once loaded.
  let inflight: Promise<void> | null = null

  function loadMe(force = false): Promise<void> {
    if (loaded.value && !force) return Promise.resolve()
    if (inflight) return inflight
    inflight = (async () => {
      try {
        user.value = await api.get<MeResponse>('/api/v1/me')
      } catch {
        // Unauthenticated, or API not yet available: treat as anonymous context.
        user.value = null
      } finally {
        loaded.value = true
        inflight = null
      }
    })()
    return inflight
  }

  async function mfaEnabled(login: string): Promise<boolean> {
    // Backend contract: GET /auth/mfa-enabled?login=<username-or-email> -> { mfaRequired }
    const r = await api.get<{ mfaRequired: boolean }>(
      `/api/v1/auth/mfa-enabled?login=${encodeURIComponent(login)}`,
    )
    return r.mfaRequired
  }

  async function login(username: string, password: string, totpCode?: string): Promise<void> {
    user.value = await api.post<MeResponse>('/api/v1/auth/login', { username, password, totpCode })
    loaded.value = true
  }

  async function logout(): Promise<void> {
    await api.post('/api/v1/auth/logout')
    user.value = null
  }

  // New-account creation. On success the server signs the user in (session cookie) and returns
  // the fresh MeResponse, mirroring the legacy /register auto-login.
  interface RegisterPayload {
    email: string
    firstName: string
    lastName: string
    password: string
    passwordConfirm: string
    recaptchaToken: string
  }
  async function register(payload: RegisterPayload): Promise<void> {
    user.value = await api.post<MeResponse>('/api/v1/auth/register', payload)
    loaded.value = true
  }

  // Forgot-password request. Always resolves (the server returns 204 whether or not an account
  // matched) — the caller shows the same neutral confirmation either way.
  async function forgotPassword(username: string, firstname: string): Promise<void> {
    await api.post('/api/v1/auth/forgot-password', { username, firstname })
  }

  async function recaptchaSiteKey(): Promise<string> {
    const r = await api.get<{ siteKey: string }>('/api/v1/auth/recaptcha-site-key')
    return r.siteKey
  }

  function setFromMe(me: MeResponse | null) {
    user.value = me
  }

  return {
    user,
    loaded,
    isAuthenticated,
    role,
    isAdmin,
    loadMe,
    mfaEnabled,
    login,
    logout,
    register,
    forgotPassword,
    recaptchaSiteKey,
    setFromMe,
  }
})
